import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { setPage, setHideLayout, setScrollY, setPageOpenAuto, setPageCloseAuto, setScrollTargetId } from 'app/store/modules/page';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';

export default function usePage() {
	const ua = useSsrUserAgent();
	const { pageType, hideLayout, scrollY, pageClass, scrollTargetId } = useSelector((state) => state.page, shallowEqual);
	const dispatch = useDispatch();
	const history = useHistory();

	const setPageInfo = useCallback((flag) => dispatch(setPage(flag)), [dispatch]);
	const setHideLayoutInfo = useCallback((flag) => dispatch(setHideLayout(flag)), [dispatch]);
	const setScrollYInfo = useCallback((y) => dispatch(setScrollY(y)), [dispatch]);
	const setPageOpenAutoInfo = useCallback((payload) => dispatch(setPageOpenAuto(payload)), [dispatch]);
	const setPageCloseAutoInfo = useCallback((payload) => dispatch(setPageCloseAuto(payload)), [dispatch]);
	const setScrollTargetIdInfo = useCallback((name) => dispatch(setScrollTargetId(name)), [dispatch]);

	const handlePageOpen = useCallback(
		(type) => {
			setPageInfo(type);
			setHideLayoutInfo(true);
		},
		[setPageInfo, setHideLayoutInfo],
	);

	const handlePageClose = useCallback(() => {
		setPageInfo('');
		setHideLayoutInfo(false);
	}, [setPageInfo, setHideLayoutInfo]);

	const handleScrollY = useCallback(
		(y) => {
			setScrollYInfo(y);
		},
		[setScrollYInfo],
	);

	const handleGoScroll = useCallback((id, option = 0) => {
		if (!id) return;
		const element = document.querySelector(id);
		if (!element) return;
		const scrollItemTop = element.getBoundingClientRect().top - option;
		window.scrollTo({
			top: scrollItemTop,
			behavior: 'smooth',
		});
	}, []);

	const handleScrollTargetId = useCallback(
		(id) => {
			setScrollTargetIdInfo(id);
		},
		[setScrollTargetIdInfo],
	);

	// animation
	const notSupport = ua().lessThanAndroid(5);
	const handlePageOpenAuto = useCallback(
		(type, url, force, notSupportCase) => {
			if (pageType && !force) return;
			if (notSupport || notSupportCase) {
				history.push(url);
				return;
			}
			setPageOpenAutoInfo({
				pageType: type,
				pageClass: force ? 'on_no_animation' : 'on',
				scrollY: window.pageYOffset,
			});
			if (url) window.history.pushState('usePageOpen', null, url);
			setHideLayoutInfo(true);
			window.scrollTo(0, 0);
		},
		[notSupport, history, setHideLayoutInfo, setPageOpenAutoInfo, pageType],
	);

	const handlePageCloseAuto = useCallback(
		(url) => {
			setPageCloseAutoInfo({
				pageClass: 'end',
				hideLayout: false,
			});
			setPageInfo('');
			if (url) window.history.pushState('usePageClose', null, url);
			// 본문(hide 해제)이 다시 그려진 뒤에 복원해야 한다. 오버레이가 남아있는 시점에
			// 동기로 scrollTo하면 문서 높이가 짧아 스크롤이 클램프된다.
			requestAnimationFrame(() => window.scrollTo(0, scrollY));
		},
		[scrollY, setPageCloseAutoInfo, setPageInfo],
	);

	const handlePageCloseInit = useCallback(() => {
		setPageInfo('');
		setHideLayoutInfo(false);
		setScrollYInfo(0);
	}, [setPageInfo, setHideLayoutInfo, setScrollYInfo]);

	const switchPageClose = useCallback(
		(url) => {
			if (pageType) {
				handlePageCloseAuto(url);
			} else {
				window.history.back();
			}
		},
		[handlePageCloseAuto, pageType],
	);

	const switchPageOpen = useCallback(
		(url, pageTypeName) => {
			if (pageType) {
				handlePageOpenAuto(pageTypeName, url);
			} else {
				history.push(url);
			}
		},
		[history, handlePageOpenAuto, pageType],
	);

	const loadCompletePage = hideLayout || pageType === '';

	return {
		pageType,
		pageClass,
		hideLayout,
		scrollY,
		handlePageOpen,
		handlePageClose,
		handleScrollY,
		handlePageOpenAuto,
		handlePageCloseAuto,
		handlePageCloseInit,
		switchPageClose,
		switchPageOpen,
		loadCompletePage,
		handleGoScroll,
		handleScrollTargetId,
		scrollTargetId,
	};
}
